import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "@/hooks/useAuth";
import api from "@/services/api";
import LeaveTypesModal from "@/components/LeaveTypesModal";
import EmployeeLeaveBalancesModal from "@/components/EmployeeLeaveBalancesModal";
import LeaveRequestModal from "@/components/LeaveRequestModal";
import LeaveRequestsList from "@/components/LeaveRequestsList";
import LeaveDashboard from "@/components/LeaveDashboard";
import { CalendarDays, List, Users, TrendingUp, Plus } from "lucide-react";

const tabs: { key: "overview" | "requests" | "calendar"; label: string; icon: React.ElementType }[] = [
  { key: "overview", label: "Overview", icon: TrendingUp },
  { key: "requests", label: "Leave Requests", icon: List },
  { key: "calendar", label: "Calendar", icon: CalendarDays },
];

interface LeaveType {
  id: number;
  name: string;
  code: string;
  maxDaysPerYear: number | null;
  carryForwardAllowed: boolean;
  maxCarryForwardDays: number;
  requiresApproval: boolean;
  isPaid: boolean;
  description: string | null;
  isActive: boolean;
  _count: {
    employeeLeaveBalances: number;
    leaveRequests: number;
  };
}

interface LeaveTypesResponse {
  leaveTypes: LeaveType[];
}

interface LeaveStatistics {
  totalLeaveTypes: number;
  pendingRequests: number;
  approvedThisMonth: number;
  rejectedThisMonth: number;
  totalApproved: number;
  totalRejected: number;
  month: number;
  year: number;
}

const Leaves: React.FC = () => {
  const { user } = useAuth();
  const [showLeaveTypesModal, setShowLeaveTypesModal] = useState(false);
  const [showBalancesModal, setShowBalancesModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [activeTab, setActiveTab] = useState<"overview" | "requests" | "calendar">("overview");

  const canManageLeaveTypes =
    user?.role === "admin" || user?.role === "hr_manager";
  const canViewLeaves = [
    "admin",
    "hr_manager",
    "hr_staff",
    "manager",
    "employee",
  ].includes(user?.role || "");

  const { data: leaveTypesData, isLoading: leaveTypesLoading, refetch } =
    useQuery<LeaveTypesResponse>({
      queryKey: ["leaveTypes"],
      queryFn: async () => {
        const response = await api.get("/leaves/types");
        return response.data.data;
      },
      enabled: canManageLeaveTypes,
    });

  const { data: leaveStatistics, isLoading: statsLoading } =
    useQuery<LeaveStatistics>({
      queryKey: ["leaveStatistics"],
      queryFn: async () => {
        const response = await api.get("/leaves/statistics");
        return response.data.data;
      },
    });

  if (!canViewLeaves) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="h-16 w-16 mx-auto bg-indigo-50 dark:bg-indigo-900/20 rounded-full flex items-center justify-center mb-4">
            <CalendarDays className="text-indigo-600 dark:text-indigo-400 h-6 w-6" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Access Denied</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            You don't have permission to access leave management.
          </p>
        </div>
      </div>
    );
  }

  const getStatusBadge = (isActive: boolean) => (
    <span
      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold border ${
        isActive
          ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800"
          : "bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800"
      }`}
    >
      {isActive ? (
        <>
          <CheckCircleIcon className="w-3 h-3 mr-1" /> Active
        </>
      ) : (
        <>
          <XCircleIcon className="w-3 h-3 mr-1" /> Inactive
        </>
      )}
    </span>
  );

  const getApprovalBadge = (requiresApproval: boolean) => (
    <span
      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold border ${
        requiresApproval
          ? "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800"
          : "bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-900/20 dark:text-indigo-400 dark:border-indigo-800"
      }`}
    >
      {requiresApproval ? "Requires Approval" : "Auto-Approved"}
    </span>
  );

  return (
    <div className="max-w-7xl mx-auto space-y-5">
      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-3 mb-4 lg:mb-0">
          <div className="h-10 w-10 flex items-center justify-center rounded-lg bg-indigo-50 dark:bg-indigo-900/20">
            <CalendarDays className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
              Leave Management
            </h1>
            <p className="text-gray-500 dark:text-gray-400 text-xs">
              Manage leave types, requests, and employee balances
            </p>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => setShowRequestModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium transition-all duration-200 hover:shadow-md"
          >
            <Plus size={14} /> Apply for Leave
          </button>

          {canManageLeaveTypes && (
            <>
              <button
                onClick={() => setShowLeaveTypesModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-gray-200 text-gray-700 text-sm font-medium transition-all duration-200 hover:shadow-md dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <Plus size={14} /> Add Leave Type
              </button>
              <button
                onClick={() => setShowBalancesModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-gray-200 text-gray-700 text-sm font-medium transition-all duration-200 hover:shadow-md dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <Users size={14} /> View Balances
              </button>
            </>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-6">
          {tabs.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-xs flex items-center gap-2 transition-all duration-200 ${
                activeTab === key
                  ? "border-indigo-600 text-indigo-600"
                  : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600"
              }`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === "overview" && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {
                title: "Leave Types",
                value: leaveStatistics?.totalLeaveTypes || 0,
                icon: CalendarDays,
                iconBg: "bg-indigo-50 dark:bg-indigo-900/20",
                iconColor: "text-indigo-600 dark:text-indigo-400",
              },
              {
                title: "Pending Requests",
                value: leaveStatistics?.pendingRequests || 0,
                icon: ClockIcon,
                iconBg: "bg-amber-50 dark:bg-amber-900/20",
                iconColor: "text-amber-600 dark:text-amber-400",
              },
              {
                title: "Approved This Month",
                value: leaveStatistics?.approvedThisMonth || 0,
                icon: CheckCircleIcon,
                iconBg: "bg-emerald-50 dark:bg-emerald-900/20",
                iconColor: "text-emerald-600 dark:text-emerald-400",
              },
              {
                title: "Rejected This Month",
                value: leaveStatistics?.rejectedThisMonth || 0,
                icon: XCircleIcon,
                iconBg: "bg-red-50 dark:bg-red-900/20",
                iconColor: "text-red-600 dark:text-red-400",
              },
            ].map((stat, index) => (
              <div
                key={index}
                className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-card"
              >
                <div className="flex items-center justify-between mb-3">
                  <div
                    className={`h-10 w-10 rounded-lg flex items-center justify-center ${stat.iconBg}`}
                  >
                    {React.createElement(stat.icon, { className: `h-5 w-5 ${stat.iconColor}` })}
                  </div>
                </div>
                <p className="text-lg font-bold text-gray-900 dark:text-white mb-1">{statsLoading ? "..." : stat.value}</p>
                <p className="text-xs text-gray-600 dark:text-gray-400 font-medium">{stat.title}</p>
              </div>
            ))}
          </div>

          {/* Leave Types List */}
          {canManageLeaveTypes && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-card p-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                  Leave Types
                </h2>
                <button
                  onClick={() => setShowLeaveTypesModal(true)}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-200 bg-indigo-600 hover:bg-indigo-700 text-white"
                >
                  <Plus size={12} /> Add Leave Type
                </button>
              </div>

              {leaveTypesLoading ? (
                <div className="text-center py-6">
                  <div className="h-8 w-8 rounded-full border-[3px] border-indigo-600 border-t-transparent animate-spin mx-auto mb-2"></div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Loading leave types...</p>
                </div>
              ) : !leaveTypesData?.leaveTypes?.length ? (
                <div className="text-center py-6">
                  <CalendarDays className="mx-auto h-6 w-6 text-gray-400 dark:text-gray-500 mb-2" />
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">No Leave Types</h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">Get started by creating your first leave type</p>
                  <button
                    onClick={() => setShowLeaveTypesModal(true)}
                    className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-200 bg-indigo-600 hover:bg-indigo-700 text-white"
                  >
                    <Plus size={12} /> Create Leave Type
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {leaveTypesData.leaveTypes.map((lt) => (
                    <div key={lt.id} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 border border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 transition-all duration-200">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-semibold text-gray-900 dark:text-white text-sm">
                              {lt.name}
                            </p>
                            <span className="text-xs bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300 rounded px-1.5 py-0.5 font-medium">
                              {lt.code}
                            </span>
                          </div>
                          <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                            {lt.description || "No description available"}
                          </p>
                          <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                            <span className={`font-medium ${lt.isPaid ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}>
                              {lt.isPaid ? "Paid" : "Unpaid"}
                            </span>
                            <span>-</span>
                            <span>{lt.maxDaysPerYear || "Unlimited"} days/year</span>
                            <span>-</span>
                            <span>{lt._count.employeeLeaveBalances} employees</span>
                            <span>-</span>
                            <span>{lt._count.leaveRequests} requests</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {getStatusBadge(lt.isActive)}
                          {getApprovalBadge(lt.requiresApproval)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {activeTab === "requests" && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-card overflow-hidden">
          <LeaveRequestsList />
        </div>
      )}

      {activeTab === "calendar" && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-card overflow-hidden">
          <LeaveDashboard />
        </div>
      )}

      {/* Modals */}
      {showLeaveTypesModal && (
        <LeaveTypesModal
          onClose={() => setShowLeaveTypesModal(false)}
          onSuccess={() => {
            setShowLeaveTypesModal(false);
            refetch();
          }}
        />
      )}
      {showBalancesModal && (
        <EmployeeLeaveBalancesModal
          onClose={() => setShowBalancesModal(false)}
        />
      )}
      {showRequestModal && (
        <LeaveRequestModal
          onClose={() => setShowRequestModal(false)}
          onSuccess={() => setActiveTab("requests")}
        />
      )}
    </div>
  );
};

export default Leaves;